/**
 * AnswerMC.tsx
 * Student view for answering a live Multiple Choice question.
 * Subscribes to room for config changes and submits answers.
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { subscribeToRoom } from '@db/roomService';
import { submitAnswer } from '@db/submissionService';
import { ensureAnonymousAuth, getCurrentUid } from '@db/firebase';
import type { MCConfig } from '@types-local/quiz.types';
import type { Room } from '@types-local/room.types';

const OPTION_COLORS = [
  'from-brand-500 to-brand-700',
  'from-orange-500 to-orange-700',
  'from-green-500 to-green-700',
  'from-purple-500 to-purple-700',
];
const OPTION_LABELS = ['A', 'B', 'C', 'D'];

export default function AnswerMC(): React.ReactElement {
  const [room, setRoom] = useState<Room | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [startTime, setStartTime] = useState(Date.now());

  const studentName = sessionStorage.getItem('gh_student_name') ?? 'Student';
  const roomCode = sessionStorage.getItem('gh_room_code') ?? '';
  const classId = sessionStorage.getItem('gh_class_id') ?? '';

  useEffect(() => {
    ensureAnonymousAuth();
    if (!roomCode) return;
    return subscribeToRoom(roomCode, setRoom);
  }, [roomCode]);

  const config = room?.activeConfig?.type === 'multiple_choice'
    ? (room.activeConfig as MCConfig)
    : null;

  // Reset for each new question so students flow through a multi-slide
  // session instead of being stuck on the previous answer/result.
  useEffect(() => {
    setSelected(null);
    setSubmitted(false);
    setStartTime(Date.now());
  }, [config?.question]);

  // Timer countdown
  useEffect(() => {
    if (!config?.timeLimit) return;
    setTimeLeft(config.timeLimit);
    const interval = setInterval(() => {
      setTimeLeft((t) => (t !== null && t > 0 ? t - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [config?.timeLimit, config?.question]);

  const handleSelect = async (index: number) => {
    if (submitted || !config || !room?.activeSlideId) return;
    setSelected(index);
    setSubmitted(true);

    const uid = getCurrentUid() ?? 'anon';
    await submitAnswer(roomCode, uid, studentName, room.activeSlideId, {
      type: 'multiple_choice',
      selectedIndex: index,
      timeToAnswerMs: Date.now() - startTime,
    }, classId);
  };

  if (!config) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center space-y-4">
        <div className="text-5xl animate-bounce">⏳</div>
        <h2 className="text-xl font-bold text-slate-900">Waiting for teacher…</h2>
        <p className="text-sm text-slate-500">Your teacher will start an activity soon</p>
        <p className="text-xs font-mono text-brand-400">{roomCode}</p>
      </div>
    );
  }

  // Teacher revealed the answer — show the student their result.
  if (room?.status === 'results') {
    const correctIndex = config.options.findIndex((o) => o.isCorrect);
    const gotIt = selected !== null && selected === correctIndex;
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center space-y-4">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 400 }}
          className="text-6xl">{gotIt ? '🎉' : '❌'}</motion.div>
        <h2 className="text-xl font-bold text-slate-900">
          {gotIt ? 'Correct!' : selected !== null ? 'Not quite' : 'Time’s up'}
        </h2>
        <p className="text-sm text-slate-600">
          Correct answer:{' '}
          <strong className="text-green-400">
            {OPTION_LABELS[correctIndex]}. {config.options[correctIndex]?.text}
          </strong>
        </p>
        {gotIt && (
          <p className="text-sm text-yellow-400 font-semibold">+{config.starValue} ⭐</p>
        )}
        <p className="text-xs text-slate-500">Waiting for the next question…</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center space-y-4">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 400 }}
          className="text-6xl">✅</motion.div>
        <h2 className="text-xl font-bold text-slate-900">Answer Submitted!</h2>
        <p className="text-sm text-slate-600">You answered <strong className="text-slate-900">{OPTION_LABELS[selected!]}</strong></p>
        <p className="text-xs text-slate-500">Waiting for others…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col p-4 pb-8 max-w-lg mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between py-3 mb-4">
        <span className="text-xs text-slate-500 font-mono">{roomCode}</span>
        {timeLeft !== null && (
          <div className={`text-sm font-bold px-3 py-1 rounded-full border ${timeLeft <= 5 ? 'text-red-400 border-red-500/30 bg-red-500/10 animate-pulse' : 'text-brand-300 border-brand-500/30 bg-brand-500/10'}`}>
            ⏱ {timeLeft}s
          </div>
        )}
      </div>

      {/* Question */}
      <div className="glass rounded-3xl p-5 mb-6">
        <p className="text-xs text-brand-400 font-semibold mb-2">📊 Multiple Choice</p>
        <h2 className="text-lg font-bold text-slate-900 leading-snug">{config.question}</h2>
      </div>

      {/* Options */}
      <div className="grid grid-cols-1 gap-3 flex-1">
        {config.options.map((opt, i) => (
          <motion.button
            key={opt.id}
            onClick={() => handleSelect(i)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.07 }}
            className={`w-full py-4 px-5 rounded-2xl text-left text-base font-semibold text-white flex items-center gap-3 bg-gradient-to-r ${OPTION_COLORS[i]} shadow-lg`}
          >
            <span className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center text-sm font-black flex-shrink-0">
              {OPTION_LABELS[i]}
            </span>
            {opt.text}
          </motion.button>
        ))}
      </div>
    </div>
  );
}
